import { format, parse } from "date-fns";
import { ja } from "date-fns/locale";
import {
  type ICommercialFeedResponse,
  type ICommercialFeedDataGridRow,
  type IMaskUnlockResponse,
} from "../types";
import { MASK_UNLOCK_MAX_IDS } from "../constants";

// 受付帳の値（12桁の会社法人等番号 or 検査用数字付き13桁の法人番号、全角混在あり）を
// 民事法務協会API仕様の12桁の会社法人等番号に正規化する。正規化できない場合はnullを返す。
export const toCommercialRegistrationNumber = (raw: string): string | null => {
  const digits = raw
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/\D/g, "");
  if (digits.length === 12) return digits;
  if (digits.length === 13) return digits.slice(1);
  return null;
};

/**
 * 商業登記フィードのデータをGridに表示するデータの型に変換する関数
 * @param {ICommercialFeedResponse | undefined} data
 * @returns {ICommercialFeedDataGridRow[]}
 */
export const toCommercialFeedGridRows = (
  data: ICommercialFeedResponse | undefined
): ICommercialFeedDataGridRow[] => {
  if (data === undefined) return [];
  return data.list.map((item) => {
    return {
      id: item.id,
      isCorporate: item.isCorporate ? "法人" : "商業",
      pageNumber: item.pageNumber,
      legalAffairsBureauRequestDate: item.legalAffairsBureauRequestDate
        ? format(
            parse(item.legalAffairsBureauRequestDate, "yyyy-MM-dd", new Date()),
            "yyyy/MM/dd",
            { locale: ja }
          )
        : "",
      legalAffairsBureauReceptionNumber:
        item.legalAffairsBureauReceptionNumber ?? "",
      requesterCorporateName: item.requesterCorporateName ?? "",
      requesterCommercialRegistrationNumber:
        item.requesterCommercialRegistrationNumber ?? "",
      requesterCorporateNumber: item.requesterCorporateNumber ?? "",
      receptionReason: item.receptionReason ?? "",
      isPurchased: item.isPurchased,
      remarks: item.remarks ?? "",
      prefecturesId: item.prefecturesId,
      prefecturesName: item.prefecturesName ?? "",
      legalAffairsBureauName: item.legalAffairsBureauName ?? "",
      commercialRequesterTypeName: item.commercialRequesterTypeName ?? "",
      receptionReasons: item.receptionReasons?.join(", ") ?? "",
    };
  });
};

/**
 * マスク解除済みの会社法人等番号・法人番号・法人名を保持するマップの型。
 * key は行ID、値はマスク解除APIが返した解除後の値（null もあり得る）。
 */
export type CommercialUnlockedRowMap = Record<
  number,
  {
    requesterCorporateName: string | null;
    requesterCommercialRegistrationNumber: string | null;
    requesterCorporateNumber: string | null;
  }
>;

/**
 * マスク解除済みの情報を元の行データにマージする。
 * 解除済みの行は会社法人等番号・法人番号・法人名を解除後の値（null は空文字へ正規化）で
 * 上書きし、isPurchased を true にする。未解除の行はそのまま返す。
 * @param {ICommercialFeedDataGridRow[]} rows
 * @param {CommercialUnlockedRowMap} unlockedRows
 * @returns {ICommercialFeedDataGridRow[]}
 */
export const mergeUnlockedRows = (
  rows: ICommercialFeedDataGridRow[],
  unlockedRows: CommercialUnlockedRowMap
): ICommercialFeedDataGridRow[] => {
  return rows.map((row) => {
    const unlocked = unlockedRows[row.id];
    if (!unlocked) return row;
    return {
      ...row,
      requesterCorporateName: unlocked.requesterCorporateName ?? "",
      requesterCommercialRegistrationNumber:
        unlocked.requesterCommercialRegistrationNumber ?? "",
      requesterCorporateNumber: unlocked.requesterCorporateNumber ?? "",
      isPurchased: true,
    };
  });
};

/**
 * マスクされている（未購入の）行のIDセットを返す。
 * 購入済み（マスク解除済み）の行は含まれない。
 * @param {ICommercialFeedDataGridRow[]} rows
 * @returns {Set<number>}
 */
export const getMaskedRowIds = (
  rows: ICommercialFeedDataGridRow[]
): Set<number> => {
  return new Set(rows.filter((row) => !row.isPurchased).map((row) => row.id));
};

/** 一括閲覧（マスク解除）ボタンの有効/無効状態とツールチップ文言。 */
export interface MaskUnlockButtonState {
  disabled: boolean;
  tooltipTitle: string;
}

/**
 * 一括閲覧（マスク解除）ボタンの有効/無効状態とツールチップ文言を算出する。
 * 選択中のマスク済み行数が0件、または読み込み中の場合はボタンを無効化する。
 * バックエンドの上限（MASK_UNLOCK_MAX_IDS）を超えて選択されている場合も無効化し、
 * 理由をツールチップ文言として返す。
 * @param {number} selectedMaskedIdsCount - 選択中のマスク済み行のID数
 * @param {boolean} loading - フィードのロード中かどうか
 * @returns {MaskUnlockButtonState} ボタンの有効/無効状態とツールチップ文言
 */
export const getMaskUnlockButtonState = (
  selectedMaskedIdsCount: number,
  loading: boolean
): MaskUnlockButtonState => {
  if (selectedMaskedIdsCount > MASK_UNLOCK_MAX_IDS) {
    return {
      disabled: true,
      tooltipTitle: `一度に解除できるのは${MASK_UNLOCK_MAX_IDS}件までです。選択を減らしてください`,
    };
  }
  return {
    disabled: loading || selectedMaskedIdsCount === 0,
    tooltipTitle: "マスクされた行を選択してください",
  };
};

/**
 * マスク解除の合計金額（単価 × 件数）を算出する。
 * 単価が未取得（null）の場合は算出できないため null を返す。
 * @param {number} count - 対象件数
 * @param {number | null} unitPrice - 単価（未取得の場合はnull）
 * @returns {number | null}
 */
export const calculateTotalPrice = (
  count: number,
  unitPrice: number | null
): number | null => {
  if (unitPrice === null) return null;
  return count * unitPrice;
};

/**
 * マスク解除APIのレスポンス件数がリクエスト件数と一致しているかを検証し、
 * 一致しない場合はユーザーへ提示するメッセージを返す。
 * 一致する場合（かつリクエスト件数が1件以上）は null を返す。
 * バックエンドは全件成功/全件失敗（非2xx）の二択で部分成功は発生しない想定だが、
 * 想定外の乖離を検知した際に「実解除件数と異なるのに成功扱いで閉じる」ことを防ぐ。
 * @param {IMaskUnlockResponse} response
 * @param {number} requestedCount
 * @returns {string | null}
 */
export const getUnlockMismatchMessage = (
  response: IMaskUnlockResponse,
  requestedCount: number
): string | null => {
  if (requestedCount > 0 && response.list.length === requestedCount) {
    return null;
  }
  return `${requestedCount}件中${response.list.length}件のみ解除されました。画面を再読み込みして状態をご確認のうえ、必要に応じて再度お試しください。`;
};

/**
 * マスク解除APIのレスポンスを解除済みマップにマージした新しいマップを返す。
 * 既存のマップは変更せず、新しいオブジェクトを返す（イミュータブル）。
 * @param {CommercialUnlockedRowMap} prev
 * @param {IMaskUnlockResponse} response
 * @returns {CommercialUnlockedRowMap}
 */
export const applyUnlockResponse = (
  prev: CommercialUnlockedRowMap,
  response: IMaskUnlockResponse
): CommercialUnlockedRowMap => {
  const next = { ...prev };
  response.list.forEach((item) => {
    next[item.id] = {
      requesterCorporateName: item.requesterCorporateName,
      requesterCommercialRegistrationNumber:
        item.requesterCommercialRegistrationNumber,
      requesterCorporateNumber: item.requesterCorporateNumber,
    };
  });
  return next;
};
